import { createSelector } from 'reselect';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import filter from 'lodash/filter';
import some from 'lodash/some';
import map from 'lodash/map';
import flatten from 'lodash/flatten';
import size from 'lodash/size';
import hasIn from 'lodash/hasIn';
import compact from 'lodash/compact';
import cloneDeep from 'lodash/cloneDeep';
import includes from 'lodash/includes';
import extend from 'lodash/extend';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import overEvery from 'lodash/overEvery';
import intersection from 'lodash/intersection';
import memoize from 'lodash/memoize';
import {
    initProductOptions,
    isFilterEssential,
    isSkuLot,
} from 'yoda-site-components/lib/selectors/ProductOptionsSelector';
import ProductConstants, { productDimensions } from '../../helpers/ProductConstants';

const COLOR_SKU_TOKEN = 'COLOR';
const product = {};
const returnProduct = {};

let productDetailsID = '';

export const isSelectedOptionsEqual = (filtered) => ({ optionValues }) =>
    intersection(
        map(optionValues, ({ name, value }) => `${name};${value}`),
        map(filtered, ({ name, value }) => `${name};${value}`)
    ).length === filtered.length;

function createProductObject(productID) {
    product[productID] = {
        currentFilters: [],
        filteredItems: [],
        dimensions: [],
        updatedLotDimension: [],
        isProductInStock: true,
        lotDimension: [],
        allItems: [],
        details: {},
        inventory: [],
        priceURL: {},
        images: [],
        isItemInStock: true,
        selectedProductLot: [],
        selectedLotDetails: [],
        isProductHasLot: false,
    };
}

/**
 * [setCurrentFilters will set the lot or if single dimension available set in currentfilters]
 * @return {[product.currentFilters]} [return option id and dimension name]
 */
function setCurrentFilters(featureFlags = {}) {
    const { dimensions, currentFilters, ppFeatureColor } = product[productDetailsID];
    // Loop though the dimensions array and prepare data for faster lookup.
    forEach(dimensions, ({ options, type, id, name: dimensionName }) => {
        const filterOption = filter(
            options,
            (optionValue) => optionValue.availabilityMessage !== ''
        );
        const isTypeAvailable = some(currentFilters, { type: id });
        const ppFeatureColorAvailable = filterOption.findIndex(
            (element) => element.value.toLowerCase() === ppFeatureColor.toLowerCase()
        );
        /* istanbul ignore next */
        if (!isTypeAvailable && (type === 'lot' || filterOption.length === 1)) {
            const option = {
                option: filterOption[0].value,
                type: id,
                name: filterOption[0].value,
                dimensionName,
            };
            product[productDetailsID].currentFilters = [
                ...product[productDetailsID].currentFilters,
                option,
            ];
        } else if (
            get(featureFlags, 'enablePPFeatureFlag', true) &&
            !isTypeAvailable &&
            dimensionName === 'color' &&
            ppFeatureColorAvailable !== -1
        ) {
            const option = {
                option: ppFeatureColor,
                type: id,
                name: ppFeatureColor,
                dimensionName: 'color',
            };
            product[productDetailsID].currentFilters = [
                ...product[productDetailsID].currentFilters,
                option,
            ];
        }
    });
}

/**
 * [initInventory will return all atp true items]
 * @return {[product.allItems]} [description]
 */
/* istanbul ignore next */
function initInventory() {
    /* istanbul ignore next */
    let inventory = [];
    forEach(product[productDetailsID].allItems, (item) => {
        const matchedInventory = find(product[productDetailsID].inventory, { id: item.id });
        inventory = [...inventory, matchedInventory];
    });
    product[productDetailsID].isProductInStock = some(inventory, { atp: true });
    if (product[productDetailsID].isProductInStock) {
        // Merge in the inventory data into the allItems array so we don't have to do the
        // lookups multiple times.
        forEach(product[productDetailsID].allItems, (item, key) => {
            const matchedInventory = find(product[productDetailsID].inventory, { id: item.id });
            extend(product[productDetailsID].allItems[key], matchedInventory);
        });
    } else {
        product[productDetailsID].isItemInStock = false;
    }
}

/**
 * getDimensionNameByOptionValue
 */
function getDimensionNameByOption(option, dimensionName) {
    /* istanbul ignore next */
    const [{ id = '' } = {}] = filter(
        get(product, [productDetailsID, 'dimensions'], []),
        ({ name, options }) => name === dimensionName && some(options, { value: option })
    );

    return id || '';
}

/**
 * [calculateFilterStatus will check current option Value is available or not]
 * @param  {[type]}  filterToCheck [option Value]
 * @return {Boolean}               [isItemAvailable]
 */
/* istanbul ignore next */
function calculateFilterStatus(
    filterToCheck,
    { type: dimensionType, updateType: dimensionUpdateType, id: dimensionId, name: dimensionName },
    featureFlags
) {
    let filterValues = [];
    const filterStatus = {};
    filterStatus.outOfStock = false;
    filterStatus.unavailable = false;
    filterStatus.unavailableOOS = false;
    filterStatus.notAvailable = false;
    const isLotAvailable = product[productDetailsID].currentFilters.find(
        (element) =>
            element.type !== productDimensions.COLOR &&
            !ProductConstants.SIZE_DIMENSIONS.includes(element.type)
    );
    const newFilters = get(featureFlags, 'enableSKUAvailabilityLogic', true)
        ? product[productDetailsID].currentFilters
        : filter(
              product[productDetailsID].currentFilters,
              ({ type, exclude }) =>
                  !exclude &&
                  isFilterEssential(type, {
                      id: dimensionId,
                      type: dimensionType,
                      updateType: dimensionUpdateType,
                  })
          );

    for (let i = 0; i < newFilters.length; i += 1) {
        /* istanbul ignore next */
        if (dimensionName !== newFilters[i].dimensionName) {
            filterValues.push({ value: newFilters[i].option, name: newFilters[i].dimensionName });
        }
    }
    filterValues.push({ value: filterToCheck, name: dimensionName });
    const isLotDimension = includes(product[productDetailsID].lotDimension, dimensionId);
    if (get(featureFlags, 'enableSKUAvailabilityLogic', true) && isLotDimension) {
        if (
            product[productDetailsID].currentFilters.length ===
            product[productDetailsID].dimensions.length
        ) {
            if (isLotAvailable && Object.keys(isLotAvailable)) {
                if (
                    isSkuLot(dimensionType, dimensionUpdateType) &&
                    isLotAvailable.name !== filterToCheck
                ) {
                    filterValues = filterValues.filter(
                        (filter) =>
                            !ProductConstants.SIZE_DIMENSIONS.includes(filter.name.toUpperCase())
                    );
                }
            }
        }
    }
    const availableItemsAtpTrue = some(
        product[productDetailsID].allItems,
        overEvery([({ atp }) => atp, isSelectedOptionsEqual(filterValues)])
    );
    if (!availableItemsAtpTrue) {
        const availableItems = some(
            product[productDetailsID].allItems,
            overEvery([
                isSelectedOptionsEqual(filterValues),
                ({ id }) => find(product[productDetailsID].inventory, { id }),
            ])
        );
        /* istanbul ignore next */
        if (get(featureFlags, 'enableSKUAvailabilityLogic', true)) {
            let notAvailableSize = [];
            if (
                isLotAvailable &&
                Object.keys(isLotAvailable) &&
                ProductConstants.SIZE_DIMENSIONS.includes(dimensionId)
            ) {
                notAvailableSize = product[productDetailsID].allItems
                    .filter((data) =>
                        data.optionValues.find((obj) => obj.value === isLotAvailable.name)
                    )
                    .filter((data1) =>
                        data1.optionValues.find(
                            (obj1) =>
                                ProductConstants.SIZE_DIMENSIONS.includes(
                                    obj1.name.toUpperCase()
                                ) && obj1.value === filterToCheck
                        )
                    );
            }
            if (availableItems) {
                filterStatus.outOfStock = true;
            } else if (
                dimensionId === productDimensions.COLOR ||
                (notAvailableSize && notAvailableSize.length > 0)
            ) {
                filterStatus.notAvailable = true;
            } else {
                filterStatus.unavailableOOS = true;
            }
        } else if (availableItems) {
            const lengthWithoutColor = find(product[productDetailsID].dimensions, { id: 'COLOR' })
                ? product[productDetailsID].dimensions.length - 1
                : product[productDetailsID].dimensions.length;
            if (
                !isSkuLot(dimensionType, dimensionUpdateType) ||
                newFilters.length <= lengthWithoutColor
            ) {
                filterStatus.outOfStock = true;
            }
        } else {
            filterStatus.unavailable = true;
        }
    }

    return filterStatus;
}

/**
 * [setSelectedProductLot description]
 */
function setSelectedProductLot(filterValues) {
    const lots = get(product[productDetailsID], 'details.lots', []);
    if (filterValues) {
        /* istanbul ignore next */
        product[productDetailsID].selectedProductLot = filter(lots, (obj) =>
            find(obj.items, isSelectedOptionsEqual(filterValues))
        );
    } else {
        product[productDetailsID].selectedProductLot = filter(lots, { isDefault: true });
    }
}

/**
 * [setFilterStatus will go the calculation to find the filter status and
 * 1). hide the option that is Unavailable
 * 2). attach the OOS message to option if OOS]
 * @param {[type]} dimensionType [description]
 */
/* istanbul ignore next */
function setFilterStatus(dimensionType, featureFlags = {}) {
    const inventoryLength = product[productDetailsID].inventory
        ? product[productDetailsID].inventory.length
        : 0;
    forEach(product[productDetailsID].dimensions, (dimension) => {
        forEach(dimension.options, (option) => {
            const filterStatus = calculateFilterStatus(option.value, dimension, featureFlags);
            /* istanbul ignore next */
            if (
                product[productDetailsID].dimensions.length === 1 ||
                dimensionType !== dimension.id
            ) {
                option.availableInStock = true;
                option.availabilityMessage = option.value;
                option.unavailable = false;
                if (inventoryLength) {
                    /* istanbul ignore next */
                    if (
                        filterStatus.unavailable &&
                        !isSkuLot(dimension.type, dimension.updateType)
                    ) {
                        option.availableInStock = false; // eslint-disable-line no-param-reassign
                        option.availabilityMessage = '';
                    } else if (filterStatus.outOfStock) {
                        option.availableInStock = false; // eslint-disable-line no-param-reassign
                        option.availabilityMessage = `${option.value} - ${ProductConstants.OUT_OF_STOCK_MESSAGE}`;
                    }

                    if (get(featureFlags, 'enableSKUAvailabilityLogic', true)) {
                        if (filterStatus.unavailableOOS) {
                            option.unavailable = true;
                            option.availableInStock = false; // eslint-disable-line no-param-reassign
                            option.availabilityMessage =
                                !isSkuLot(dimension.type, dimension.updateType) &&
                                dimension.id !== productDimensions.LENGTH // length dimension shouldn't be filtered out
                                    ? ''
                                    : `${option.value} - ${productDimensions.NOTAVAILABLE}`;
                        } else if (filterStatus.notAvailable) {
                            option.unavailable = true;
                            option.availableInStock = false; // eslint-disable-line no-param-reassign
                            option.availabilityMessage =
                                dimension.id === productDimensions.COLOR
                                    ? `${option.value} - ${ProductConstants.OUT_OF_STOCK_MESSAGE}`
                                    : `${option.value} - ${productDimensions.NOTAVAILABLE}`;
                        }
                    }
                }
            } else if (!hasIn(option, 'availableInStock')) {
                option.availableInStock = true;
                option.availabilityMessage = option.value;
            }
        });
    });
}

/**
 * [setFilteredItems To set the filtereditems based on current filter/s]
 */
/* istanbul ignore next */
function setFilteredItems(featureFlags) {
    const filterValues = [];
    forEach(product[productDetailsID].currentFilters, ({ option: value, dimensionName: name }) => {
        if (!find(filterValues, { name })) {
            filterValues.push({ value, name });
        }
    });
    product[productDetailsID].filteredItems = filter(
        product[productDetailsID].allItems,
        isSelectedOptionsEqual(filterValues)
    );
    forEach(product[productDetailsID].currentFilters, (filtering) => {
        setFilterStatus(filtering.type, featureFlags);
    });
}

/**
 * [removeFilters description]
 * @param  {[type]} filtersToBeRemoved [description]
 * @return {[type]}                    [description]
 */
/* istanbul ignore next */
function removeFilters(filtersToBeRemoved) {
    product[productDetailsID].currentFilters = filter(
        product[productDetailsID].currentFilters,
        (option) => option.type !== filtersToBeRemoved
    );
}

/* istanbul ignore next */
function isDimensionALot(dimensionType) {
    return (
        includes(product[productDetailsID].lotDimension, dimensionType) ||
        includes(product[productDetailsID].updatedLotDimension, dimensionType)
    );
}

/**
 * [addFilters, it will push all selected dimension to an array]
 * @param  {[type]} dimension name
 * @param  {[value]} dimension value
 */
/* istanbul ignore next */
function addFilters(
    dimension,
    value,
    featureFlags = { enableSKUAvailabilityLogic: true },
    filteredValue = false
) {
    const selectedFilter =
        dimension &&
        find(product[productDetailsID].currentFilters, { dimensionName: dimension.dimensionName });
    /* istanbul ignore else */
    if (dimension && value && !selectedFilter) {
        const option = {
            option: value,
            type: get(dimension, 'type'),
            name: get(dimension, 'options.value'),
            dimensionName: get(dimension, 'dimensionName'),
        };
        product[productDetailsID].currentFilters = [
            ...product[productDetailsID].currentFilters,
            option,
        ];

        /* istanbul ignore next */
        if (
            product &&
            product[productDetailsID] &&
            product[productDetailsID].inventory &&
            product[productDetailsID].inventory.length &&
            !isSkuLot(dimension.type, dimension.updateType)
        ) {
            const filterValues = map(
                product[productDetailsID].currentFilters,
                ({ option: filterValue, dimensionName: name }) => ({ value: filterValue, name })
            );
            const availableItems = some(
                product[productDetailsID].allItems,
                overEvery([
                    isSelectedOptionsEqual(filterValues),
                    ({ id }) => find(product[productDetailsID].inventory, { id }),
                ])
            );

            if (!availableItems) {
                const toBeReset = product[productDetailsID].currentFilters.reduce(
                    (resetFilter, currentFilter) => {
                        if (!resetFilter || resetFilter === COLOR_SKU_TOKEN) {
                            if (
                                currentFilter.dimensionName !== dimension.dimensionName &&
                                !isDimensionALot(currentFilter.type)
                            ) {
                                resetFilter = currentFilter.type;
                            }
                        }
                        return resetFilter;
                    },
                    null
                );
                if (get(featureFlags, 'enableSKUAvailabilityLogic', true)) {
                    if (
                        filteredValue &&
                        toBeReset === productDimensions.SIZE &&
                        dimension.type !== productDimensions.COLOR
                    ) {
                        removeFilters(toBeReset);
                    }
                } else {
                    removeFilters(toBeReset);
                }
            }
        }
    }
    setFilteredItems(featureFlags);
    setCurrentFilters(featureFlags);
}

/**
 * getAllLotsDimension
 */
function getAllLotsDimension() {
    const lotAvailableDimension = filter(product[productDetailsID].dimensions, { type: 'lot' });
    const updatedLotAvailableDimension = filter(product[productDetailsID].dimensions, {
        updateType: 'lot',
    });
    /* istanbul ignore next */
    forEach(lotAvailableDimension, ({ id }) => {
        product[productDetailsID].lotDimension.push(id);
    });
    /* istanbul ignore next */
    forEach(updatedLotAvailableDimension, ({ id }) => {
        product[productDetailsID].updatedLotDimension.push(id);
    });
}

/**
 * [calculatePricingURL description]
 * @param  {[type]} URL [description]
 * @return {[type]}     [description]
 */
function calculatePricingURL(URL) {
    const filteredItems = get(product[productDetailsID], 'filteredItems', []);
    const skuId = filteredItems.length === 1 ? get(filteredItems, '[0].id', '') : '';
    if (!URL) {
        product[productDetailsID].priceURL = {
            itemOfferingCall: false,
            selectedSKU: skuId,
        };
    } else {
        const isCustomOption =
            product[productDetailsID].selectedProductLot[0] &&
            product[productDetailsID].selectedProductLot[0].customizationModel;
        product[productDetailsID].priceURL = {
            URL,
            itemOfferingCall: true,
            isCustomOption,
            selectedSKU: skuId,
        };
    }
}

/* istanbul ignore next */
function setSelectedLotDetails() {
    const filterValues = map(
        product[productDetailsID].currentFilters,
        ({ option: value, dimensionName: name }) => ({ value, name })
    );

    product[productDetailsID].selectedLotDetails =
        filter(product[productDetailsID].details.lots, (obj) =>
            some(obj.items, isSelectedOptionsEqual(filterValues))
        )[0] || product[productDetailsID].selectedLotDetails;
}

/* istanbul ignore next */
function setItemOfferingsPriceURL(filtersToBeReset) {
    let offeringsPriceURL;
    const isLotDimension = includes(product[productDetailsID].lotDimension, filtersToBeReset);
    const isUpdatedLotDimension = includes(
        product[productDetailsID].updatedLotDimension,
        filtersToBeReset
    );
    if (product[productDetailsID].dimensions.length === 0) {
        offeringsPriceURL =
            product[productDetailsID].allItems[0] &&
            product[productDetailsID].allItems[0].offering.href;
        calculatePricingURL(offeringsPriceURL);
    } else if (
        product[productDetailsID].dimensions.length ===
        product[productDetailsID].currentFilters.length
    ) {
        const filterValues = map(
            product[productDetailsID].currentFilters,
            ({ option: value, dimensionName: name }) => ({ value, name })
        );

        let availableItems = find(
            product[productDetailsID].filteredItems,
            overEvery([({ atp }) => atp, isSelectedOptionsEqual(filterValues)])
        );

        if (!availableItems) {
            availableItems = find(product.filteredItems, isSelectedOptionsEqual(filterValues));
        }

        offeringsPriceURL =
            availableItems && availableItems.offering ? availableItems.offering.href : '';
        setSelectedProductLot(filterValues);
        calculatePricingURL(offeringsPriceURL);
    } else if (isLotDimension || isUpdatedLotDimension || filtersToBeReset === 'initial') {
        calculatePricingURL(offeringsPriceURL);
        setSelectedLotDetails();
    }
}

/* istanbul ignore next */
function getOptionDetailsByOption(option, dimensionName) {
    const type = getDimensionNameByOption(option, dimensionName);

    let optionDetails = {};
    if (type !== '') {
        const dimension = find(
            product[productDetailsID].dimensions,
            (dimens) => dimens.id === type
        );
        const options = find(dimension.options, { value: option });
        optionDetails = { type, dimensionName: dimension.name, options };
    }
    return optionDetails;
}

/* istanbul ignore next */
function setItemInStockStatus(option, dimensionName = '') {
    if (size(product[productDetailsID].currentFilters) > 0) {
        const optionsDetails = getOptionDetailsByOption(option, dimensionName);
        product[productDetailsID].isItemInStock =
            (optionsDetails.options && optionsDetails.options.availableInStock) || false;
    }
}

/* istanbul ignore next */
function setItemImages(dimension = {}, featureFlags = {}) {
    const { details } = product[productDetailsID];

    if (dimension.type === productDimensions.COLOR) {
        const cloneImage = cloneDeep(details.images);
        const altImages = get(dimension, 'options.altImages', []);

        if (cloneImage) {
            const primaryImage = get(dimension, 'options.productImage')
                ? get(dimension, 'options.productImage.url')
                : get(product, 'details.images[0].url');

            cloneImage[0].url = primaryImage;
            const { changeAltImagesColor = true } = featureFlags;
            if (altImages.length && changeAltImagesColor) {
                map(altImages, (image, index) => {
                    if (cloneImage[index + 1]) {
                        cloneImage[index + 1].url = image.url;
                    } else {
                        cloneImage[index] = {
                            url: image.url,
                            type: 'ALTERNATIVE',
                            altText: altImages[0].altText,
                        };
                    }
                });
            }
        }

        product[productDetailsID].images = cloneImage;
    }
}

function getCurrentFilterColorOptionDetails() {
    const currentColorFilter = find(product[productDetailsID].currentFilters, {
        type: productDimensions.COLOR,
    });
    return currentColorFilter ? getOptionDetailsByOption(currentColorFilter.option) : {};
}

/* istanbul ignore next */
function setLotDimensions() {
    const { details } = product[productDetailsID];

    if (details.dimensions && details.dimensions.length && details.dimensions.length > 1) {
        const dimensions = [...details.dimensions];
        const lotAvailableDimension = filter(product[productDetailsID].dimensions, { type: 'lot' });

        /* istanbul ignore next */
        if (!lotAvailableDimension.length) {
            forEach(dimensions, (dimension, index) => {
                if (index === 0) {
                    dimension.updateType = 'lot';
                    dimension.updated = true;
                }
            });
        }
    }
}

export const getProductDetails = (state, index) => {
    let productDetails;
    /* istanbul ignore next */
    if (
        state.productDetails &&
        state.productDetails.products &&
        state.productDetails.products[index]
    ) {
        productDetails = state.productDetails.products[index].product;
    } else {
        productDetails = state.productDetails;
    }
    return productDetails;
};
export const getInventory = (state) => state.bundleInventory;
export const getChangeFilter = (state) => state.productOptions;
export const getFeatureFlags = (state) => state.context && state.context.featureFlags;
// returns the selectedSKUs on page load only when all the SKUs are selected.
const getSelectedSKUsOnLoad = (state) => state.bundleProductOptionsSelectedOnLoad || [];

/* istanbul ignore next */
export const makeInitProductOption = () =>
    createSelector(
        [getProductDetails, getInventory, getChangeFilter, getFeatureFlags, getSelectedSKUsOnLoad],
        (productDetails, inventory, changeFilter, featureFlags, selectedSKUsOnLoad) => {
            /* istanbul ignore else */
            productDetailsID = productDetails.id;
            let selectedOption = [];
            // Change Filter contains the sku information that was selected manually
            /* selectedSKUsOnLoad contains the sku information that was auto selected on page load.
                Applicable only for child items with single dimension and a sku is auto selected from that dimension.
            */
            const isSKUSelectedOnCurrChild = some(changeFilter, { productId: productDetailsID });
            if (isSKUSelectedOnCurrChild) {
                selectedOption = filter(
                    changeFilter,
                    (data) => data.productId === productDetails.id
                );
            } else if (selectedSKUsOnLoad.length > 0) {
                const detailsAvailable = find(selectedSKUsOnLoad, { productId: productDetailsID });
                selectedOption = isEmpty(detailsAvailable)
                    ? []
                    : detailsAvailable.selectedDimensions;
            }

            if (selectedOption.length) {
                const { option, dimensionName } = selectedOption[0];
                const dimension = getOptionDetailsByOption(option, dimensionName);
                if (dimension.type) {
                    removeFilters(dimension.type);
                    if (get(featureFlags, 'enableSKUAvailabilityLogic', true)) {
                        addFilters(dimension, option, featureFlags, true);
                    } else {
                        addFilters(dimension, option, featureFlags);
                    }
                    setItemOfferingsPriceURL(dimension.type);
                    setItemInStockStatus(option, dimensionName);
                    setItemImages(dimension, featureFlags);
                }
            } else if (productDetails && productDetails.id) {
                createProductObject(productDetailsID);
                product[productDetailsID].details = productDetails;
                product[productDetailsID].inventory =
                    inventory && inventory[0] ? inventory[0].inventory : '';
                product[productDetailsID].dimensions = productDetails.dimensions
                    ? productDetails.dimensions
                    : [];
                product[productDetailsID].allItems = compact(
                    flatten(map(product[productDetailsID].details.lots, 'items'), true)
                );
                product[productDetailsID].filteredItems = product[productDetailsID].allItems;
                product[productDetailsID].ppFeatureColor = productDetails.ppFeatureColor
                    ? productDetails.ppFeatureColor
                    : '';
                setLotDimensions();
                getAllLotsDimension();
                if (product[productDetailsID].inventory !== '') {
                    initInventory();
                }
                setCurrentFilters(featureFlags);
                /* istanbul ignore next */
                if (product[productDetailsID].currentFilters.length === 0) {
                    setFilterStatus(null, featureFlags);
                } else {
                    setFilteredItems(featureFlags);
                    if (product[productDetailsID].dimensions.length === 1) {
                        setItemInStockStatus(
                            product[productDetailsID].dimensions[0].options[0].value
                        );
                    }
                }
                setItemOfferingsPriceURL('initial');
                setItemImages(getCurrentFilterColorOptionDetails(), featureFlags);
                setSelectedProductLot();
                setSelectedLotDetails();
                product[productDetailsID].isProductHasLot = product[
                    productDetailsID
                ].dimensions.some(({ type = '' }) => type === 'lot');
            }
            product[productDetailsID].currentFilters.forEach((currFilter) => {
                const productDimension = filter(
                    productDetails.dimensions,
                    (currProductDimension) => currProductDimension.id === currFilter.type
                );
                const filterStatus = calculateFilterStatus(
                    currFilter.option,
                    productDimension[0],
                    featureFlags
                );
                if (filterStatus.unavailable) {
                    removeFilters(productDimension[0].id);
                    const dimension = {
                        type: currFilter.type,
                        dimensionName: currFilter.dimensionName,
                        options: productDimension[0].options,
                    };
                    addFilters(dimension, null, featureFlags);
                }
            });
            returnProduct[productDetailsID] = {
                optionsSelected: product[productDetailsID].currentFilters,
                dimensions: product[productDetailsID].dimensions,
                pricingURL: product[productDetailsID].priceURL,
                filteredItems: product[productDetailsID].filteredItems,
                selectedProductLot: product[productDetailsID].selectedProductLot,
                productImages: product[productDetailsID].images,
                isProductInStock: product[productDetailsID].isProductInStock,
                selectedLotDetails: product[productDetailsID].selectedLotDetails,
                isItemInStock: product[productDetailsID].isItemInStock,
                isProductHasLot: product[productDetailsID].isProductHasLot,
                ppFeatureColor: product[productDetailsID].ppFeatureColor,
            };
            return returnProduct;
        }
    );

const getInitProductOptionForBundles = (state) => {
    if (state.productDetails && state.productDetails.products) {
        return returnProduct;
    }
    return {};
};

export const selectedProductsForBundles = createSelector(
    [getInitProductOptionForBundles],
    (productOptions) => {
        if (productOptions && !isEmpty(productOptions)) {
            return productOptions;
        }
        return null;
    }
);

export const getBundleQuantity = (state) => state.bundleQuantity;
export const getSelectedProtectionPlan = (state) => state.bundleProtectionPlan;

export const selectSkuIds = createSelector(
    [
        selectedProductsForBundles,
        getBundleQuantity,
        getChangeFilter,
        initProductOptions(false),
        (_, isBundle) => isBundle,
    ],
    (selectedProducts = {}, bundleQuantity = {}, _, productOptions, isBundle) => {
        let ids = [];
        if (isBundle) {
            ids = map(selectedProducts, ({ isItemInStock, filteredItems = [] }, id) => {
                if (bundleQuantity[id] && isItemInStock && filteredItems.length) {
                    return filteredItems[0].id;
                }
                return null;
            });
        } else {
            ids = [get(productOptions, 'filteredItems[0].id')];
        }
        return compact(ids);
    }
);

export const selectSkuIdsForBundle = createSelector(
    [selectedProductsForBundles, getChangeFilter],
    (selectedProducts = {}, _) => {
        let ids = [];
        ids = map(selectedProducts, ({ filteredItems = [] }, id) => {
            const optionsSelected = filter(
                _,
                (filterItem) => filterItem.type !== 'COLOR' && filterItem.productId === id
            );
            if (optionsSelected.length && filteredItems.length) {
                return filteredItems[0].id;
            }
            return null;
        });
        return ids;
    }
);

export const getSelectedQunatity = createSelector([getBundleQuantity], (bundleQuantity) =>
    Object.keys(bundleQuantity).length
        ? Object.values(bundleQuantity).reduce((init, qty) => init + qty, 0)
        : 0
);

/* istanbul ignore next */
export const getProductOptionsSelection = createSelector(
    [getBundleQuantity, selectedProductsForBundles, getSelectedProtectionPlan, getChangeFilter],
    (bundleQuantity = {}, selectedProducts, selectedProtectionPlan = {}, changeFilter = []) => {
        selectedProducts = selectedProducts || {};
        const getRequiredProductsIDs = Object.keys(selectedProducts).filter(
            (key) => bundleQuantity[key] !== 0
        );
        const getRequiredProtection = getRequiredProductsIDs
            .map((key) => {
                const warrantiesObj = get(
                    selectedProducts[key],
                    'selectedLotDetails.warranties',
                    []
                ).filter(({ selected }) => !selected); // Removing the default warranty object
                return warrantiesObj.length ? warrantiesObj : '';
            })
            .filter(Boolean);

        const isEverySkuSelected = changeFilter.length
            ? !!(
                  getRequiredProductsIDs
                      .map((key) => selectedProducts[key].filteredItems.length)
                      .every((len) => len === 1) && changeFilter.length
              )
            : !!getRequiredProductsIDs
                  .map((key) => selectedProducts[key].filteredItems.length)
                  .every((len) => len === 1);

        const isEveryProtectionSelected =
            getRequiredProtection.length === Object.keys(selectedProtectionPlan).length;

        return {
            isEverySkuSelected,
            isEveryProtectionSelected,
            isEveryDimensionSelected: isEverySkuSelected && isEveryProtectionSelected,
        };
    }
);

export const getSelectedProductLot = memoize(() =>
    createSelector(
        [getProductDetails, makeInitProductOption()],
        (productDetails, productOptions) => {
            let selectedLot;
            if (productDetails && productDetails.id) {
                const selectedOptions = get(
                    productOptions,
                    `${productDetails.id}.optionsSelected`,
                    null
                );
                selectedLot = filter(productDetails.lots, { isDefault: true });
                /* istanbul ignore next */
                if (selectedOptions.length) {
                    const filterValues = map(
                        selectedOptions,
                        ({ option: value, dimensionName: name }) => ({ name, value })
                    );
                    selectedLot = filter(productDetails.lots, (obj) =>
                        find(obj.items, isSelectedOptionsEqual(filterValues))
                    );
                }
            }
            return selectedLot;
        }
    )
);
